/* eslint-disable @typescript-eslint/no-misused-promises */
import { useCallback, useEffect, useRef } from 'react';

import {
  PRINTTY_ELEMENT_CLASS_ID,
  PRINTTY_ELEMENT_DEBUG_DATA_ATTRIBUTE,
  PRINT_FRAME_ID,
  PRINT_FRAME_ROOT_ID,
} from '../constants';
import { PrintConstructor } from '../printConstructor';
import { CreateTargetOptions } from '../types';
import { createNodeObserver } from '../utils/create-node-observer';
import { createDynamicSpacing } from '../utils/createDynamicSpacing';
import { frameSizeByOrientation, getFrameSizeInMm } from '../utils/getIframeSizingByOrientation';
import { injectDefaultPrinterStyles } from '../utils/injectDefaultPrinterStyles';
import { injectDefaultStyles } from '../utils/injectDefaultStyles';
import { useRenderer } from './useRenderer';

/**
 * Awaits for all fonts to be loaded from the current document
 */
const ensureFontsAreLoaded = async (contentDocument: Document) => {
  const ready = await contentDocument.fonts.ready;
  return ready;
};

/**
 * Hook to create a print target
 */
const useCreatePrinttyTarget = ({
  HeaderElement,
  HeaderInfoBoxElement,
  orientation = 'portrait',
  debug = false,
  headerProps,
  gap = 0,
  targetMargin = 0,
}: CreateTargetOptions = {}) => {
  /** The target iframe reference */
  const targetIframeRef = useRef<Window | null>(null);

  /** The html target ref we want to insert into the frame  */
  const targetRef = useRef<HTMLDivElement | null>(null);

  /** The react Node target ref we want to insert into the frame */
  const targetReactRef = useRef<React.ReactNode | null>(null);

  const { renderToTheVirtualDom, cleanUp } = useRenderer();

  const cleanupTargetFrame = useCallback(
    (overrideDebug = false) => {
      if (debug === true && !overrideDebug) {
        return;
      }
      const iFrame = document.getElementById(PRINT_FRAME_ID);
      if (iFrame) {
        document.body.removeChild(iFrame);
      }
      cleanUp();
    },
    [cleanUp, debug]
  );

  /**
   * Sets the targetReactRef
   */
  const setTargetReactRef = useCallback((node: React.ReactNode) => {
    targetReactRef.current = node;
  }, []);

  /**
  /**
   * Creates a target frame for rendering the print content
   * Once created inserts and renders the content from the targetRef
   *
   * @returns {HTMLIFrameElement} The created iframe element
   */
  const createTargetFrame = useCallback(() => {
    // Remove any existing frame before adding a new one
    cleanupTargetFrame();

    const targetFrame = document.createElement('iframe');

    // Structure the frame
    targetFrame.id = PRINT_FRAME_ID;
    targetFrame.srcdoc = '<!DOCTYPE html>';

    targetFrame.style.position = 'absolute';
    targetFrame.style.zIndex = '10000';

    if (debug == false) {
      targetFrame.style.top = `-${document.documentElement.clientHeight + 100}px`;
      targetFrame.style.left = `-${document.documentElement.clientWidth + 100}px`;
    } else {
      const { pixels } = frameSizeByOrientation(orientation);
      targetFrame.style.top = `100px`;
      targetFrame.style.left = `100px`;
      targetFrame.style.width = `${pixels.width}px`;
      targetFrame.style.height = `${pixels.height}px`;
    }

    return targetFrame;
  }, [cleanupTargetFrame, debug, orientation]);

  const insertTarget = useCallback(
    async (printBody?: Document) => {
      return new Promise<HTMLDivElement>((resolve) => {
        const { rootDiv } = renderToTheVirtualDom(
          <PrintConstructor
            contentRef={targetRef}
            HeaderElement={HeaderElement}
            headerProps={headerProps}
            HeaderInfoBoxElement={HeaderInfoBoxElement}
            content={targetReactRef.current}
            targetMargin={targetMargin}
          />,
          printBody
        );

        rootDiv.id = PRINT_FRAME_ROOT_ID;

        if (debug) {
          rootDiv.setAttribute(`data-${PRINTTY_ELEMENT_DEBUG_DATA_ATTRIBUTE}`, 'true');
        }

        return resolve(rootDiv);
      });
    },
    [HeaderElement, HeaderInfoBoxElement, debug, headerProps, renderToTheVirtualDom, targetMargin]
  );

  /**
   * Injects both printing and default styles into the print body
   * This is needed so that any styles applied to the main document are also applied to the print document
   */
  const injectStyles = useCallback(
    (printBody: Document) => {
      injectDefaultStyles(printBody, orientation, getFrameSizeInMm(orientation)); // Applies styles without media tags
      injectDefaultPrinterStyles(printBody, orientation); // Applies styles with media tags
    },
    [orientation]
  );

  /**
   * Fetches all the styles from the main document and returns them as a document fragment
   *
   * TODO: Load style sheets before resolving the promise
   * This will fix edge cases where the styles are not loaded in time for the print target to be created
   */
  const fetchStyles = useCallback(async () => {
    const fragment = new DocumentFragment();
    const arrStyleSheets = document.querySelectorAll(
      "style, link[rel~='stylesheet'], link[as='style']"
    );
    const styleSheets = Array.from(arrStyleSheets);

    await styleSheets.reduce(async (promise, styleSheet) => {
      await promise;
      fragment?.appendChild(styleSheet.cloneNode(true));
      return Promise.resolve();
    }, Promise.resolve());

    return fragment;
  }, []);

  /**
   * Spaces required elements to prevent them from running into the margins
   * and to ensure that the pages are not fragmented
   * Calls the resolve function when the spacing is complete
   */
  const spaceRequiredElements = useCallback(
    async (contentDocument: Document, resolveSpaceRequiredElements: () => void) => {
      /**
       * If the content document body has elements with the printty element class id
       * We can now add spacing between them to prevent them from running into the margins
       * and to ensure that the pages are not fragmented
       */
      const elements = contentDocument.body.querySelectorAll(`.${PRINTTY_ELEMENT_CLASS_ID}`);
      if (elements.length > 0) {
        const pageHeight = frameSizeByOrientation(orientation).pixels.height;
        await createDynamicSpacing(elements, pageHeight, gap, debug, contentDocument);
      }

      resolveSpaceRequiredElements();
    },
    [debug, gap, orientation]
  );
  /**
   * Gets the html node from the target document
   */
  const getHTMLNode = useCallback((targetDocument = document) => {
    return targetDocument.head.parentNode as HTMLHtmlElement;
  }, []);

  /**
   * Creates a target for printing
   * injects styles and renders the content from the target ref,
   * and returns the created iframe
   * @returns {Promise<HTMLIFrameElement>}
   */
  const createTarget = useCallback(async () => {
    // return a new promise
    return new Promise<HTMLIFrameElement>((resolveCreateTarget, reject) => {
      void (async () => {
        try {
          const targetFrame = createTargetFrame();
          const bodyFragment = new DocumentFragment();

          const rootDiv = await insertTarget();
          bodyFragment.appendChild(rootDiv);

          const styles = await fetchStyles();

          targetFrame.addEventListener('load', async function () {
            const { contentWindow, contentDocument } = targetFrame;

            if (!contentWindow || !contentDocument) return;

            injectStyles(contentDocument);

            targetIframeRef.current = this.contentWindow;
            const htmlNode = getHTMLNode();
            const contentDocumentHTMLElement = getHTMLNode(contentDocument);

            contentDocumentHTMLElement.dataset.mantineColorScheme =
              htmlNode.dataset.mantineColorScheme;
            contentDocument.head.appendChild(styles);

            // Double requestAnimationFrame to ensure the styles are loaded
            // the first requestAnimationFrame is to ensure the styles are added to the document head
            // the second requestAnimationFrame is to ensure the styles are loaded /applied to the document
            await new Promise((resolveStyles) =>
              contentWindow.requestAnimationFrame(() =>
                contentWindow.requestAnimationFrame(resolveStyles)
              )
            );

            // At this point the styles are loaded and added to the document head,
            // so we can create a mutation observer to detect when the body has been appended to the document
            // Before we continue because in the next step we'll start resolving printty elements
            const { disconnect } = await new Promise<{ disconnect: () => void }>(
              (resolveBodyAppended) => {
                const { disconnect } = createNodeObserver({
                  onObservedChange: () => resolveBodyAppended({ disconnect }),
                  node: contentDocument.body,
                  window: contentWindow,
                });

                contentDocument.body.appendChild(bodyFragment);
              }
            );

            // Disconnect the observer as we no longer need it
            disconnect();

            await ensureFontsAreLoaded(contentDocument);

            await spaceRequiredElements(contentDocument, () => {
              resolveCreateTarget(targetFrame);
            });
          });

          document.body.appendChild(targetFrame);
        } catch {
          reject(new Error('Error creating print target'));
        }
      })();
    });

    // All dependencies must be static, so we can add them all
  }, [
    createTargetFrame,
    insertTarget,
    fetchStyles,
    injectStyles,
    getHTMLNode,
    spaceRequiredElements,
  ]);

  // Cleanup the iFrame
  useEffect(() => {
    return () => {
      cleanupTargetFrame();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Do not want to run this on every render, only on unmount
  }, []);

  return {
    createTarget,
    targetRef,
    cleanupTargetFrame,
    targetIframeRef,
    targetReactRef,
    setTargetReactRef,
  };
};

export { useCreatePrinttyTarget };
